import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { shortCard } from '../format';
import { t } from '../i18n';
import { NOT_A_RULE, cardLink, ruleLink, splitSetRule } from '../links';
import { usePrimarySetId } from '../hooks/usePrimarySetId';

/**
 * The click-throughs every decision list uses (UX review P1-3): a card opens its decisions in
 * Investigate, a rule id opens that rule in Rule Management. Short on screen, full in the link.
 */

/**
 * The card id as the console shows it everywhere (UX review P2-13, product owner 30 Sep): its
 * short tail (`shortCard`), the full value in the tooltip, and a copy button that copies the
 * full value. The card id is a token, not a PAN; links and URLs carry it in full.
 */
export function CardCopy({ cardId }: { cardId: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);
  const label = copied ? t('links.card.copied') : t('links.card.copy.a11y');
  return (
    <button
      type="button"
      className="copybtn"
      aria-label={label}
      title={label}
      data-copied={copied || undefined}
      // In a row that opens something else, copying must not also open the row.
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(cardId).then(
          () => setCopied(true),
          () => undefined,
        );
      }}
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
}

/** A card's short tail with the full id as its tooltip and a copy button — no link. */
export function CardId({ cardId, testid }: { cardId: string | null | undefined; testid?: string }) {
  if (!cardId) return <span className="muted">—</span>;
  return (
    <span className="cardid nowrap">
      <span className="mono" title={cardId} data-testid={testid}>
        {shortCard(cardId)}
      </span>
      <CardCopy cardId={cardId} />
    </span>
  );
}

/** A card's short tail, linking to its decisions and cases by the FULL id (also the tooltip), with a copy button. */
export function CardLink({ cardId, testid }: { cardId: string | null | undefined; testid?: string }) {
  if (!cardId) return <span className="muted">—</span>;
  return (
    <span className="cardid nowrap">
      {/* A link in a row that opens something else must not also open the row. */}
      <Link to={cardLink(cardId)} title={t('links.card.title', { card: cardId })} data-testid={testid} onClick={(e) => e.stopPropagation()}>
        {shortCard(cardId)}
      </Link>
      <CardCopy cardId={cardId} />
    </span>
  );
}

/** Rule ids, comma-separated, each linking to its rule; the fail-safe marker stays text. */
export function RuleLinks({ ids, testid }: { ids: readonly string[]; testid?: string }) {
  const primaryId = usePrimarySetId();
  if (ids.length === 0) return <span className="muted">—</span>;
  return (
    <span className="mono small" data-testid={testid}>
      {ids.map((id, i) => (
        <Fragment key={id}>
          {i > 0 && ', '}
          {NOT_A_RULE.has(id) ? (
            id
          ) : (
            // 2c rule sets: `<set>/<rule>` opens the rule in its set
            <Link to={ruleLink(splitSetRule(id).ruleId, splitSetRule(id).setId, primaryId)} title={t('links.rule.title', { id })} onClick={(e) => e.stopPropagation()}>
              {id}
            </Link>
          )}
        </Fragment>
      ))}
    </span>
  );
}
